import * as THREE from 'three';
import { CAMERA_INITIAL_Z, CAMERA_ZOOM_MIN, CAMERA_ZOOM_MAX } from './globe';
import { isMobileDevice } from '../textures';

/**
 * Input controller for the globe.
 *
 * Terminology used throughout the engine:
 *   - **Pan**  — single-finger drag / mouse drag that rotates the globe
 *   - **Zoom** — wheel / trackpad / pinch that moves the camera in-out
 *
 * Design notes:
 *
 * 1. **Pan is zoom-scaled.** Raw drag delta × constant = a visual pan rate
 *    that feels fast when zoomed in (surface covers more pixels per degree)
 *    and slow when zoomed out (surface covers fewer). We scale pan
 *    sensitivity by current zoom so a fixed drag distance moves the same
 *    apparent fraction of the visible surface at any zoom level. Matches
 *    industry convention (Mapbox, Google Earth do this).
 *
 * 2. **Zoom is multiplicative.** Each wheel/pinch step applies
 *    `zoom *= exp(delta * sensitivity)` so percentage change per step is
 *    uniform across the zoom range.
 *
 * 3. **Multi-pointer tracking.** Size 1 = pan mode. Size 2 = pinch mode.
 *    Prevents "first finger rotates before second finger lands" mobile jitter.
 */
export interface ControlsState {
  rotX: number;
  rotY: number;
  targetRotX: number;
  targetRotY: number;
  zoom: number;
  targetZoom: number;
}

export interface CameraTarget {
  /** Latitude in degrees, -90 to +90. Omit to leave current rotation unchanged. */
  lat?: number;
  /** Longitude in degrees, -180 to +180. Omit to leave current rotation unchanged. */
  lon?: number;
  /**
   * Camera distance from globe center, world units. Clamped to engine's
   * zoom range. Omit to leave current zoom unchanged.
   */
  zoom?: number;
  /**
   * False to snap instantly (initial framing); true to ease via the
   * existing rotation/zoom tween. Default false.
   */
  animate?: boolean;
}

export interface ControlsHandle {
  state: ControlsState;
  tick: () => void;
  dispose: () => void;
  /**
   * Programmatically frame the globe at a (lat, lon, zoom). Sets the
   * controls' target state and (when `animate: false`) snaps the live
   * rotation + zoom to match instantly so the next render shows the
   * target framing without a 1-frame visible easing.
   */
  setCameraTarget: (target: CameraTarget) => void;
}

export interface CreateControlsOptions {
  element: HTMLElement;
  globeGroup: THREE.Group;
  camera: THREE.PerspectiveCamera;
  initialZoom: number;
  /**
   * Pan (drag-rotate) sensitivity multiplier. 1.0 = engine default.
   * Clamped to [0.1, 5.0]. Intended to be driven by a user-facing slider.
   * Auto-scaled by current zoom internally so you don't need to compensate.
   */
  panSensitivity?: number;
  /**
   * Zoom (wheel + pinch) sensitivity multiplier. 1.0 = engine default.
   * Clamped to [0.1, 5.0].
   */
  zoomSensitivity?: number;
}

// Base rotation per pixel of pointer movement. Tuned down from the
// original 0.005 after live play feedback that pans felt "flicky" —
// 0.004 (20 % drop) settles the rotation enough that small mouse
// adjustments at deep zoom stop overshooting the target city.
// Affects every device + zoom level proportionally; the per-device +
// per-zoom curves below scale this base.
const BASE_PAN_SENSITIVITY = 0.004;
const BASE_ZOOM_SENSITIVITY = 0.001;
const EASING_ROTATION = 0.12;
const EASING_ZOOM = 0.1;

/**
 * Pinch-zoom is gesturally coarse compared to a wheel notch — whole-hand
 * spread maps to large raw ratios. Damping the ratio toward 1 makes a
 * 2× finger spread translate to ~1.6× zoom, same direction with less
 * aggressive jumps at the edges of the pinch. Only applies to actual
 * pinch (size === 2 pointers); wheel/trackpad zoom keeps its raw curve.
 */
const PINCH_DAMPING = 0.65;

function clampSensitivity(value: number | undefined): number {
  if (value === undefined) return 1;
  return Math.max(0.1, Math.min(5, value));
}

function clampZoom(z: number): number {
  return Math.max(CAMERA_ZOOM_MIN, Math.min(CAMERA_ZOOM_MAX, z));
}

interface PointerPos {
  x: number;
  y: number;
}

export function createControls(options: CreateControlsOptions): ControlsHandle {
  const { element, globeGroup, camera, initialZoom } = options;

  const panSens = BASE_PAN_SENSITIVITY * clampSensitivity(options.panSensitivity);
  const zoomSens = BASE_ZOOM_SENSITIVITY * clampSensitivity(options.zoomSensitivity);

  const state: ControlsState = {
    rotX: 0,
    rotY: 0,
    targetRotX: 0,
    targetRotY: 0,
    zoom: initialZoom,
    targetZoom: initialZoom,
  };

  const pointers = new Map<number, PointerPos>();
  let pinchLastDistance = 0;

  function computePinchDistance(): number {
    if (pointers.size < 2) return 0;
    const [p1, p2] = [...pointers.values()];
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  // UA-sniffed mobile flag, captured once at construction. Used to apply a
  // tighter zoomed-in pan curve since touch drags traverse more pixels per
  // gesture than a mouse and the surface "whips" at deep zoom otherwise.
  const mobile = isMobileDevice();

  // Zoom-scaled pan. Two regimes, tuned from real-device testing:
  //
  //   - Zoomed OUT (ratio ≥ 1): linear scaling — feels right per testing,
  //     don't over-engineer the common case.
  //   - Zoomed IN (ratio < 1): exponent curve instead of linear, so pan
  //     attenuates more aggressively at close zoom. Desktop uses 1.4
  //     (mouse-precise); mobile uses 2.3 (touch-coarser + surface fills
  //     a much larger share of the screen at deep zoom on a phone, so a
  //     finger swipe of the same pixel distance covers far more visible
  //     surface — the higher exponent compensates).
  //
  // Curve is continuous at ratio=1 (both regimes return 1.0 there).
  const zoomedInExponent = mobile ? 2.3 : 1.4;
  function zoomScale(): number {
    const ratio = state.zoom / CAMERA_INITIAL_Z;
    return ratio < 1 ? Math.pow(ratio, zoomedInExponent) : ratio;
  }

  function onPointerDown(e: PointerEvent): void {
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    element.setPointerCapture(e.pointerId);
    if (pointers.size === 2) {
      pinchLastDistance = computePinchDistance();
    }
    element.style.cursor = 'grabbing'; // pan in progress
  }

  function onPointerMove(e: PointerEvent): void {
    const prev = pointers.get(e.pointerId);
    if (!prev) return;

    const curr: PointerPos = { x: e.clientX, y: e.clientY };

    if (pointers.size === 1) {
      const dx = curr.x - prev.x;
      const dy = curr.y - prev.y;
      const scale = panSens * zoomScale();
      state.targetRotY += dx * scale;
      state.targetRotX += dy * scale;
      const cap = Math.PI / 2 - 0.05;
      if (state.targetRotX > cap) state.targetRotX = cap;
      if (state.targetRotX < -cap) state.targetRotX = -cap;
    }

    pointers.set(e.pointerId, curr);

    if (pointers.size === 2) {
      const currDist = computePinchDistance();
      if (pinchLastDistance > 0 && currDist > 0) {
        const rawRatio = pinchLastDistance / currDist;
        // Damp the raw pinch ratio toward 1 — see PINCH_DAMPING comment.
        const ratio = Math.pow(rawRatio, PINCH_DAMPING);
        state.targetZoom = clampZoom(state.targetZoom * ratio);
      }
      pinchLastDistance = currDist;
    }
  }

  function onPointerUp(e: PointerEvent): void {
    pointers.delete(e.pointerId);
    element.releasePointerCapture(e.pointerId);
    if (pointers.size < 2) {
      pinchLastDistance = 0;
    }
    if (pointers.size === 0) {
      element.style.cursor = 'crosshair'; // rest
    }
  }

  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    const multiplier = Math.exp(e.deltaY * zoomSens);
    state.targetZoom = clampZoom(state.targetZoom * multiplier);
  }

  element.addEventListener('pointerdown', onPointerDown);
  element.addEventListener('pointermove', onPointerMove);
  element.addEventListener('pointerup', onPointerUp);
  element.addEventListener('pointercancel', onPointerUp);
  element.addEventListener('wheel', onWheel, { passive: false });
  // Crosshair at rest to signal "click to target" — matches the prototype.
  // Flips to `grabbing` while a pan is in progress.
  element.style.cursor = 'crosshair';
  element.style.touchAction = 'none';

  function tick(): void {
    state.rotX += (state.targetRotX - state.rotX) * EASING_ROTATION;
    state.rotY += (state.targetRotY - state.rotY) * EASING_ROTATION;
    state.zoom += (state.targetZoom - state.zoom) * EASING_ZOOM;

    globeGroup.rotation.x = state.rotX;
    globeGroup.rotation.y = state.rotY;
    camera.position.z = state.zoom;
  }

  /**
   * Convert a lat/lon into the (rotX, rotY) pair that brings that point
   * to face the camera. Derivation:
   *
   *   - Three.js applies group.rotation in XYZ order, so when set together
   *     the matrix is `R = R_x(rx) * R_y(ry)` (Z is ignored here).
   *   - latLonToVec3 places (lat, lon) at a Cartesian point P. We want
   *     `R * P = (0, 0, +1)` (the camera-facing point of the unit sphere).
   *   - Solving: rotY brings the point's azimuth to face the camera plane
   *     (z>0), then rotX brings its latitude up to the equator.
   *
   * The `-π/2` offset on rotY accounts for the texture's `lon + 180`
   * shift in latLonToVec3 (so longitude 0 lands on +X, not +Z).
   */
  function setCameraTarget(target: CameraTarget): void {
    if (target.lat !== undefined) {
      state.targetRotX = (target.lat * Math.PI) / 180;
    }
    if (target.lon !== undefined) {
      state.targetRotY = -((target.lon * Math.PI) / 180) - Math.PI / 2;
    }
    if (target.zoom !== undefined) {
      state.targetZoom = clampZoom(target.zoom);
    }
    // Clamp the latitude target so initial framing respects the same
    // tilt cap that user pans do — frees us from worrying about a region
    // near the poles producing an over-tilt that can't snap back.
    const cap = Math.PI / 2 - 0.05;
    if (state.targetRotX > cap) state.targetRotX = cap;
    if (state.targetRotX < -cap) state.targetRotX = -cap;
    if (!target.animate) {
      state.rotX = state.targetRotX;
      state.rotY = state.targetRotY;
      state.zoom = state.targetZoom;
      // Snap the live three.js objects too so the very next render frame
      // shows the new framing without a one-frame easing lag.
      globeGroup.rotation.x = state.rotX;
      globeGroup.rotation.y = state.rotY;
      camera.position.z = state.zoom;
    }
  }

  function dispose(): void {
    element.removeEventListener('pointerdown', onPointerDown);
    element.removeEventListener('pointermove', onPointerMove);
    element.removeEventListener('pointerup', onPointerUp);
    element.removeEventListener('pointercancel', onPointerUp);
    element.removeEventListener('wheel', onWheel);
    element.style.cursor = '';
    element.style.touchAction = '';
    pointers.clear();
  }

  return { state, tick, dispose, setCameraTarget };
}
